import React, { useState } from 'react';
import useMeta from '../hooks/useMeta';

export default function Kontakt() {
  useMeta(
    'Kontakt - Danry Diamant Boring ApS',
    'Kontakt Danry Diamant Boring ApS for tilbud på diamantboring, ventilationsboringer og specialopgaver.'
  );

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^\+?[0-9\s-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse skal udfyldes';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Der opstod en fejl ved afsendelse af formularen. Prøv igen.');
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        description: '',
        website_url: '',
      });
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-[90px] lg:pt-[120px] min-h-screen bg-white">
      {/* Contact Content */}
      <section className="py-16 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Contact Information */}
        <div className="lg:col-span-5 flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold text-neutral-900 tracking-tight uppercase">
              Virksomhedsoplysninger
            </h2>
            <p className="text-neutral-600 text-sm leading-relaxed">
              Du er altid velkommen til at kontakte os direkte. Vi står klar til at rådgive dig om dit næste diamantboringsprojekt.
            </p>
          </div>

          <div className="flex flex-col divide-y divide-neutral-200">
            {/* Phone */}
            <div className="flex gap-4 py-6 first:pt-0 last:pb-0">
              <div className="text-brand-orange text-2xl shrink-0 mt-1">
                <i className="fa-solid fa-phone" />
              </div>
              <div className="flex flex-col justify-center">
                <a href="tel:+4522971411" className="text-lg font-bold text-neutral-900 hover:text-brand-orange transition-colors whitespace-nowrap">
                  22 97 14 11
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex gap-4 py-6 first:pt-0 last:pb-0">
              <div className="text-brand-orange text-2xl shrink-0 mt-1">
                <i className="fa-solid fa-envelope" />
              </div>
              <div className="flex flex-col justify-center">
                <a href="mailto:danrydiamant@gmail.com" className="text-lg font-bold text-neutral-900 hover:text-brand-orange transition-colors whitespace-nowrap">
                  danrydiamant@gmail.com
                </a>
              </div>
            </div>

            {/* Address */}
            <div className="flex gap-4 py-6 first:pt-0 last:pb-0">
              <div className="text-brand-orange text-2xl shrink-0 mt-1">
                <i className="fa-solid fa-location-dot" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="text-base font-bold text-neutral-900">
                  Amager Landevej 54 1. tv.
                </span>
                <span className="text-sm text-neutral-600">2770 Kastrup</span>
              </div>
            </div>
          </div>

          {/* Extra info */}
          <div className="flex flex-col gap-2 text-xs text-neutral-500 pt-4">
            <div className="flex justify-between">
              <span>Firmanavn:</span>
              <span className="text-neutral-800 font-medium">Danry Diamant Boring ApS</span>
            </div>
            <div className="flex justify-between">
              <span>CVR-nummer:</span>
              <span className="text-neutral-800 font-medium">46118685</span>
            </div>
            <div className="flex justify-between">
              <span>Geografisk dækning:</span>
              <span className="text-neutral-800 font-medium">Hele Sjælland</span>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-7 rounded-none border border-neutral-200 bg-white p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-neutral-900 tracking-tight mb-6 uppercase">
            Send os en besked
          </h2>

          {submitStatus === 'success' && (
            <div className="p-6 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 mb-6 flex gap-4 items-start">
              <i className="fa-solid fa-circle-check text-xl mt-0.5 shrink-0 text-emerald-600" />
              <div>
                <h4 className="font-bold text-base text-emerald-900 uppercase">Tak for din henvendelse!</h4>
                <p className="text-sm mt-1">Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="p-6 rounded-xl bg-red-50 border border-red-200 text-red-800 mb-6 flex gap-4 items-start">
              <i className="fa-solid fa-triangle-exclamation text-xl mt-0.5 shrink-0 text-red-600" />
              <div>
                <h4 className="font-bold text-base text-red-900 uppercase">Der opstod en fejl</h4>
                <p className="text-sm mt-1">{errorMessage}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* Honeypot field (STRICTLY HIDDEN) */}
            <div className="sr-only absolute" aria-hidden="true">
              <label htmlFor="website_url">Do not fill this field</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                value={formData.website_url}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-sm font-semibold text-neutral-700">
                Navn <span className="text-brand-orange">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg bg-neutral-50 border ${
                  errors.name ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:ring-brand-orange focus:bg-white'
                } text-neutral-900 focus:outline-none focus:ring-2 transition-all`}
                placeholder="Dit fulde navn"
              />
              {errors.name && <span className="text-xs text-red-500 mt-1">{errors.name}</span>}
            </div>

            {/* Phone & Email Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Phone */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="phone" className="text-sm font-semibold text-neutral-700">
                  Telefonnummer <span className="text-brand-orange">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg bg-neutral-50 border ${
                    errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:ring-brand-orange focus:bg-white'
                  } text-neutral-900 focus:outline-none focus:ring-2 transition-all`}
                  placeholder="F.eks. 22971411"
                />
                {errors.phone && <span className="text-xs text-red-500 mt-1">{errors.phone}</span>}
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-semibold text-neutral-700">
                  E-mail <span className="text-brand-orange">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg bg-neutral-50 border ${
                    errors.email ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:ring-brand-orange focus:bg-white'
                  } text-neutral-900 focus:outline-none focus:ring-2 transition-all`}
                  placeholder="F.eks. navn@mail.dk"
                />
                {errors.email && <span className="text-xs text-red-500 mt-1">{errors.email}</span>}
              </div>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="description" className="text-sm font-semibold text-neutral-700">
                Beskrivelse af opgaven <span className="text-brand-orange">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                rows="5"
                value={formData.description}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg bg-neutral-50 border ${
                  errors.description ? 'border-red-500 focus:ring-red-500' : 'border-neutral-200 focus:ring-brand-orange focus:bg-white'
                } text-neutral-900 focus:outline-none focus:ring-2 transition-all resize-none`}
                placeholder="Beskriv venligst din opgave (f.eks. antal huller, dimensioner, materiale osv.)"
              />
              {errors.description && <span className="text-xs text-red-500 mt-1">{errors.description}</span>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 rounded-full bg-brand-orange hover:bg-brand-orange-hover disabled:bg-neutral-200 disabled:text-neutral-400 text-white font-bold text-base transition-colors shadow-lg shadow-brand-orange/20 flex items-center justify-center gap-3 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Sender besked...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane" />
                  Send besked
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
